/**
 * DivergingBars: positive values above the zero line, negative below, one
 * category per column, drawn on Recharts (the Wave C catalog's diverging bars).
 * The legend carries token swatches, the tooltip names both sides, and the
 * click-through contract is preserved per side through Recharts' click events.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import {
  Bar,
  BarChart as RechartsBarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, CHART_MARGIN, Legend, seriesColor, surfaceClass } from "./shared";

/** One diverging row: the category and its two sides, both as positive counts. */
export interface DivergingRow {
  readonly label: string;
  readonly positive: number;
  readonly negative: number;
}

/** Which side of the zero line was picked (the click contract carries it). */
export type DivergingSide = "positive" | "negative";

interface DivergingShapeProps {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
  readonly index?: number;
}

/** A rounded rect on the top corners (above the line) or the bottom (below). */
function roundedRect(
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  side: DivergingSide,
): string {
  const r = Math.min(radius, width / 2, Math.abs(height));
  const top = Math.min(y, y + height);
  const bottom = Math.max(y, y + height);
  if (side === "positive") {
    return [
      `M ${String(x)} ${String(bottom)}`,
      `L ${String(x)} ${String(top + r)}`,
      `Q ${String(x)} ${String(top)} ${String(x + r)} ${String(top)}`,
      `L ${String(x + width - r)} ${String(top)}`,
      `Q ${String(x + width)} ${String(top)} ${String(x + width)} ${String(top + r)}`,
      `L ${String(x + width)} ${String(bottom)}`,
      "Z",
    ].join(" ");
  }
  return [
    `M ${String(x)} ${String(top)}`,
    `L ${String(x + width)} ${String(top)}`,
    `L ${String(x + width)} ${String(bottom - r)}`,
    `Q ${String(x + width)} ${String(bottom)} ${String(x + width - r)} ${String(bottom)}`,
    `L ${String(x + r)} ${String(bottom)}`,
    `Q ${String(x)} ${String(bottom)} ${String(x)} ${String(bottom - r)}`,
    "Z",
  ].join(" ");
}

function keyboardGuard(
  onSelect: (() => void) | undefined,
): ((event: React.KeyboardEvent) => void) | undefined {
  if (onSelect === undefined) {
    return undefined;
  }
  return (event: React.KeyboardEvent): void => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };
}

export function DivergingBars({
  ariaLabel,
  data,
  categoryAxisLabel,
  onSelectRow,
  positiveSeriesLabel,
  negativeSeriesLabel,
}: {
  ariaLabel: string;
  data: readonly DivergingRow[];
  /** The category axis label (spec section 4: what and unit). Defaults to month. */
  categoryAxisLabel?: string;
  /**
   * Optional click-through (the chart contract): the picked side and its row.
   * Only wired when a natural filtered register sits behind each side.
   */
  onSelectRow?: (side: DivergingSide, row: DivergingRow, index: number) => void;
  /** Optional label for the positive series. Defaults to strings.charts.positiveSeries. */
  positiveSeriesLabel?: string;
  /** Optional label for the negative series. Defaults to strings.charts.negativeSeries. */
  negativeSeriesLabel?: string;
}): React.JSX.Element {
  const positiveLabel = positiveSeriesLabel ?? strings.charts.positiveSeries;
  const negativeLabel = negativeSeriesLabel ?? strings.charts.negativeSeries;
  const total = data.reduce((sum, one) => sum + one.positive + one.negative, 0);
  if (data.length === 0 || total === 0) {
    return <ChartEmpty />;
  }
  // The negative side is stored negated so Recharts draws it below the line.
  const rows = data.map((one) => ({
    label: one.label,
    positive: one.positive,
    negative: -one.negative,
  }));
  const shape =
    (side: DivergingSide, colorIndex: number) =>
    (props: DivergingShapeProps): React.JSX.Element | null => {
      const { x, y, width, height, index } = props;
      if (x === undefined || y === undefined || width === undefined || height === undefined) {
        return null;
      }
      const row = data[index ?? 0];
      if (row === undefined) {
        return null;
      }
      const value = side === "positive" ? row.positive : row.negative;
      if (value === 0) {
        return null;
      }
      const name = strings.charts.diverging(
        row.label,
        value,
        side === "positive" ? positiveLabel : negativeLabel,
      );
      const click =
        onSelectRow === undefined
          ? undefined
          : () => {
              onSelectRow(side, row, index ?? 0);
            };
      return (
        <path
          d={roundedRect(x, y, width, height, 6, side)}
          fill={seriesColor(colorIndex)}
          className={onSelectRow === undefined ? undefined : "q-chart__hit"}
          tabIndex={onSelectRow === undefined ? undefined : 0}
          aria-label={name}
          role="img"
          onClick={click}
          onKeyDown={keyboardGuard(click)}
        >
          <title>{name}</title>
        </path>
      );
    };

  return (
    <figure className="q-chart" data-testid="diverging-chart">
      <div className="q-chart__frame">
        <ResponsiveContainer
          width="100%"
          height="100%"
          initialDimension={{ width: 640, height: 320 }}
        >
          <RechartsBarChart
            data={rows}
            margin={CHART_MARGIN}
            role="img"
            aria-label={ariaLabel}
            ref={surfaceClass}
          >
            <CartesianGrid stroke="var(--q-rule)" vertical={false} />
            <XAxis
              dataKey="label"
              interval={0}
              tickLine={false}
              axisLine={{ stroke: "var(--q-rule-strong)" }}
              label={{
                value: categoryAxisLabel ?? strings.charts.monthAxis,
                position: "insideBottom",
                offset: -2,
              }}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              tickFormatter={(value: number): string => String(Math.abs(Math.round(value)))}
              label={{
                value: strings.charts.valueAxis,
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle" },
              }}
            />
            <ReferenceLine y={0} stroke="var(--q-rule-strong)" />
            <Tooltip
              isAnimationActive={false}
              cursor={{ fill: "var(--q-sig-wash)" }}
              content={({ active: hover, payload, label }) =>
                hover && payload.length > 0 ? (
                  <div className="q-chart__tooltip">
                    <p className="q-chart__tooltip-title">{String(label)}</p>
                    <ul className="q-chart__tooltip-list">
                      {payload.map((entry, index) => (
                        <li key={index} className="q-chart__tooltip-row">
                          <span
                            aria-hidden="true"
                            className="q-chart__tooltip-swatch"
                            style={{ background: entry.color }}
                          />
                          {entry.name}: {String(Math.abs(Number(entry.value ?? 0)))}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null
              }
            />
            <Bar
              dataKey="positive"
              name={positiveLabel}
              stackId="positive"
              fill={seriesColor(1)}
              shape={shape("positive", 1)}
              isAnimationActive={false}
              onClick={(_datum, index) => {
                const picked = data[index];
                if (onSelectRow !== undefined && picked !== undefined) {
                  onSelectRow("positive", picked, index);
                }
              }}
            />
            <Bar
              dataKey="negative"
              name={negativeLabel}
              stackId="negative"
              fill={seriesColor(2)}
              shape={shape("negative", 2)}
              isAnimationActive={false}
              onClick={(_datum, index) => {
                const picked = data[index];
                if (onSelectRow !== undefined && picked !== undefined) {
                  onSelectRow("negative", picked, index);
                }
              }}
            />
          </RechartsBarChart>
        </ResponsiveContainer>
      </div>
      <Legend
        entries={[
          { label: positiveLabel, index: 1 },
          { label: negativeLabel, index: 2 },
        ]}
      />
    </figure>
  );
}
